import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface AdminLoginHistoryEntry {
  id: string;
  user_id: string;
  user_email: string;
  username: string;
  ip_address: string;
  login_time: string;
  is_suspicious: boolean;
  latitude: number;
  longitude: number;
  country: string;
  city: string;
  user_agent: string;
  session_id: string;
}

export function useAdminLoginSessions(daysBack: number = 30) {
  const [sessions, setSessions] = useState<AdminLoginHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchAdminLoginSessions();
  }, [daysBack]);

  const fetchAdminLoginSessions = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.rpc('get_admin_login_sessions', {
        days_back: daysBack
      });

      if (error) {
        console.error('Error fetching admin login sessions:', error);
        toast({
          title: "Error loading login data",
          description: error.message,
          variant: "destructive"
        });
        return;
      }

      // Cast and transform the data to match our interface
      const transformedData: AdminLoginHistoryEntry[] = (data || []).map((session: any) => ({
        ...session,
        ip_address: String(session.ip_address || 'Unknown')
      }));

      setSessions(transformedData);
    } catch (error) {
      console.error('Error in fetchAdminLoginSessions:', error);
      toast({
        title: "Error loading login data",
        description: "Failed to fetch platform login sessions",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    totalLogins: sessions.length,
    uniqueUsers: new Set(sessions.map(s => s.user_id)).size,
    uniqueCountries: new Set(sessions.map(s => s.country).filter(Boolean)).size,
    suspiciousCount: sessions.filter(s => s.is_suspicious).length
  };

  return {
    sessions,
    loading,
    stats,
    refetch: fetchAdminLoginSessions
  };
}
